import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';
import { Coins, QrCode, ScanLine, Home, Users, Store, ArrowRight } from 'lucide-react';

export default function ComeFunziona() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo linkTo="/" size="md" />
          <div className="flex items-center gap-3">
            <Link to="/"><Button variant="ghost" size="sm">Le proposte</Button></Link>
            <Link to="/login"><Button className="bg-slate-900 hover:bg-slate-800 text-white" size="sm">Accedi</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center max-w-3xl mx-auto">
          <p className="uppercase tracking-widest text-xs text-amber-700 font-semibold mb-3">Come funziona</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 font-serif mb-5">
            Trasforma i tuoi acquisti in <span className="text-amber-600">crediti condominiali</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            AIAC Card connette residenti e aziende locali. Ogni volta che compri da un'azienda partner, accumuli punti che potrai usare per abbattere le tue spese condominiali.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-5xl mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: <ScanLine className="w-6 h-6" />, num: 1, title: "Attiva la proposta", desc: 'Dal catalogo scegli una proposta e clicca "Ottieni". Riceverai un QR code univoco.' },
            { icon: <QrCode className="w-6 h-6" />, num: 2, title: "Vai in negozio", desc: "Fai il tuo acquisto normalmente. Al momento del pagamento mostra il QR code all'esercente." },
            { icon: <Coins className="w-6 h-6" />, num: 3, title: "Accumula punti", desc: "L'esercente scansiona, tu confermi entro 24h. I punti si aggiungono al tuo account." },
          ].map((s) => (
            <div key={s.num} className="p-6 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">{s.num}</div>
                <div className="text-amber-600">{s.icon}</div>
              </div>
              <h3 className="font-serif text-xl text-slate-900 mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Punti → € */}
        <div className="mt-12 p-8 rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
              <Home className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-2xl text-slate-900 mb-2">Dai punti alle spese condominiali</h3>
              <p className="text-slate-700 leading-relaxed">
                I punti accumulati vengono convertiti in <b>crediti</b> che l'amministratore di condominio applica direttamente alle tue rate condominiali.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Chi partecipa */}
      <section className="max-w-6xl mx-auto px-4 pb-24">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-serif text-center mb-10">Chi partecipa</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: <Users className="w-6 h-6" />, title: "Residenti", desc: "Utenti che vivono in un condominio associato AIAC. Accumulano punti su ogni acquisto." },
            { icon: <Store className="w-6 h-6" />, title: "Aziende partner", desc: "Ristoranti, negozi e servizi locali che vogliono attrarre nuovi clienti del quartiere." },
            { icon: <Home className="w-6 h-6" />, title: "Amministratori di condominio", desc: "Gestiscono l'accesso dei residenti e applicano i punti alle rate condominiali." },
          ].map((c, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 mb-3">{c.icon}</div>
              <h3 className="font-serif text-xl text-slate-900 mb-1">{c.title}</h3>
              <p className="text-sm text-slate-600">{c.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link to="/">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-base" data-testid="cta-catalog-btn">
              Scopri le proposte disponibili
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <p className="mt-3 text-sm text-slate-500">
            Sei un'azienda? <Link to="/register-company" className="text-amber-700 hover:underline font-medium">Iscriviti come partner</Link>
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-sm text-slate-500">
          © AIAC Card · <Link to="/come-funziona" className="hover:underline">Come funziona</Link>
        </div>
      </footer>
    </div>
  );
}
